import {
  RULE_FIELDS,
  RULE_IN_MAX,
  RULE_OPS,
  RULE_REMOTE_FIELDS,
  type LineItemRule,
  type RuleCondition,
  type RuleField,
  type RuleOp,
  type RuleVocabularyEntry,
} from '@forecastify/shared';
import { useEffect, useRef, useState } from 'react';
import { Button, DropdownMenu, Icon, TextInput, type MenuItem } from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';
import { PanelField } from './setting-row';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C26 · The rule, edited. `2fazis.LineItemDetail.html` 245–305.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *Illeszkedik [minden|bármelyik] feltételre*, then a row per condition: field, operator, value.
 *
 * ⚠️⚠️ **EVERY CHANGE SAVES, AND THE SERVER RE-EVALUATES IN THE SAME REQUEST.** There is no *Mentés*
 * on this block, which is the mockup's shape and the right one: a rule is a filter, and a filter you
 * have to commit is a filter you cannot try. The consequence is that a half-typed condition would
 * claim tasks — which is why `matchesRule` refuses an empty value rather than treating it as a
 * wildcard. The two decisions are one decision.
 *
 * ⚠️⚠️ **…TWO SECONDS AFTER THE LAST CHANGE — 29 Oct 2026.** Each keystroke in a value saved and
 * re-read the project's tasks: *"an ugly spam with every keystroke"*. The editor keeps a draft and
 * sends it once nothing has changed for `SAVE_AFTER_MS`, whatever the change was — a typed letter, a
 * pick, a removed row. A draft still waiting when the editor goes away is sent then, not dropped.
 *
 * ⚠️ **The value control changes with the field.** Project, status, priority and assignee are ids,
 * so they are pickers over what the workspace has; tag, key and title are text. Offering a free-text
 * box for an id is how somebody types *Haystack* into a field that wants a uuid and gets a rule that
 * matches nothing, with no error anywhere.
 *
 * ⚠️ **`0160` — component, fix version and epic are pickers too**, over what the line item's project
 * has synced from Jira (`GET /line-items/:id/rule-vocabulary`). Their values are Jira ids nobody
 * could type. And **`in` takes a list**: chips for what is chosen, the same chips the exclusions
 * below use, and an add control — because `DropdownMenu` closes on every choice, so ticking several
 * boxes in one menu is not something it can do.
 */

const FIELD_KEY: Readonly<Record<RuleField, MessageKey>> = {
  project: 'contract.column.project',
  status: 'contract.column.status',
  priority: 'rule.field.priority',
  assignee: 'rule.field.assignee',
  tag: 'rule.field.tag',
  key: 'lineItem.taskKey',
  title: 'contract.tasks',
  component: 'rule.field.component',
  fix_version: 'rule.field.fixVersion',
  epic: 'rule.field.epic',
};

const OP_KEY: Readonly<Record<RuleOp, MessageKey>> = {
  eq: 'rule.op.eq',
  ne: 'rule.op.ne',
  contains: 'rule.op.contains',
  in: 'rule.op.in',
};

/**
 * ⚠️ **`contains` is not offered on a Jira field**, because what it would search is an id: *component
 * contains 100* is a condition with no meaning anybody intended.
 */
const opsFor = (field: RuleField): readonly RuleOp[] =>
  RULE_REMOTE_FIELDS.has(field) ? RULE_OPS.filter((op) => op !== 'contains') : RULE_OPS;

/**
 * The condition after its operator changes.
 *
 * ⚠️ **What was chosen carries across, it is not thrown away.** `tag = uat` switched to `in` becomes
 * `tag in [uat]`, and back again keeps the first. The server would normalise either way (`0160`
 * keeps `value` and the list apart), but a click that silently empties the row is a click that
 * looks like it lost something.
 */
function withOp(condition: RuleCondition, op: RuleOp): RuleCondition {
  if (op === 'in') {
    const list = condition.op === 'in' ? (condition.values ?? []) : [condition.value];
    return { field: condition.field, op, value: '', values: list.filter((one) => one !== '') };
  }
  const value = condition.op === 'in' ? (condition.values?.[0] ?? '') : condition.value;
  return { field: condition.field, op, value };
}

/** What a picker-valued field offers. The screen supplies them; this knows nothing about tasks. */
export interface RuleOptions {
  readonly projects: readonly {
    readonly id: string;
    readonly name: string;
    readonly color: string;
  }[];
  readonly statuses: readonly {
    readonly id: string;
    readonly name: string;
    readonly color: string;
  }[];
  readonly priorities: readonly { readonly id: string; readonly name: string }[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  /** `0160` · what the project's synced Jira issues carry. Empty until the vocabulary has loaded. */
  readonly components: readonly RuleVocabularyEntry[];
  readonly fixVersions: readonly RuleVocabularyEntry[];
  readonly epics: readonly RuleVocabularyEntry[];
}

/** How long the rule waits after the last change before it saves. */
const SAVE_AFTER_MS = 2000;

export function RuleEditor({
  rule: savedRule,
  options,
  remote = false,
  canManage,
  excluded,
  onChange,
  onRestore,
}: {
  readonly rule: LineItemRule;
  readonly options: RuleOptions;
  /**
   * `0161` · whether the line item has a tracker target. The three Jira fields only mean something
   * against a remote project's issues (`line-item-sync.md` D6), so without one they are not offered.
   */
  readonly remote?: boolean;
  readonly canManage: boolean;
  /** Tasks thrown back, as `{ id, key }` — the chips under the conditions. */
  readonly excluded: readonly { readonly id: string; readonly key: string }[];
  readonly onChange: (rule: LineItemRule) => void;
  readonly onRestore: (taskId: string) => void;
}) {
  const t = useT();
  const { rule, edit } = useDebouncedRule(savedRule, onChange);

  const setCondition = (index: number, part: Partial<RuleCondition>): void => {
    edit({
      ...rule,
      conditions: rule.conditions.map((condition, at) =>
        at === index ? { ...condition, ...part } : condition,
      ),
    });
  };

  return (
    <>
      {/* ⚠️ 29 Oct 2026 · *minden / bármelyik* is a picker inside the field's own label — the
          sentence reads as one line, and the choice sits where it is read. */}
      <PanelField
        label={
          <>
            {t('rule.conditionsLead')}
            <Picker
              width={104}
              label={t('rule.matches')}
              value={t(rule.match === 'all' ? 'rule.match.all' : 'rule.match.any')}
              disabled={!canManage}
              items={(['all', 'any'] as const).map((match) => ({
                label: t(match === 'all' ? 'rule.match.all' : 'rule.match.any'),
                checked: rule.match === match,
                onSelect: () => {
                  edit({ ...rule, match });
                },
              }))}
            />
            {t('rule.conditionsTail')}
          </>
        }
      >
        {/* ⚠️ J-9 · what an empty rule means under a target, said where somebody would otherwise add
          a `project` condition to say it (P2-32.4): no condition is the whole tracker project. */}
        {remote && rule.conditions.length === 0 ? (
          <p className="text-muted-foreground text-[11.5px]">{t('rule.wholeProject')}</p>
        ) : null}

        {/* ⚠️ 29 Oct 2026 · a GRID, so every row's pickers line up and fit: field, a 120px operator
            (*nem egyenlő*, *tartalmazza* wrapped in the 92px one), value, remove. A narrow panel
            puts the value on its own line. Past five rows the list scrolls inside the panel. */}
        <div className="@container">
          <div className="-mr-1 flex max-h-[188px] flex-col gap-1.5 overflow-y-auto pr-1">
            {rule.conditions.map((condition, index) => (
              <div
                key={index}
                className="grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1.2fr)_28px] items-center gap-1.5 @max-[380px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_28px]"
              >
                <Picker
                  grow
                  width={132}
                  label={t('rule.field')}
                  value={t(FIELD_KEY[condition.field])}
                  disabled={!canManage}
                  items={RULE_FIELDS.filter(
                    (field) =>
                      field === condition.field ||
                      /* ⚠️ J-9 · `project` is never offered: under a target the Forecastify project is
                   what routing DECIDES, so an inbound issue has none yet and the condition cannot
                   match (decisions.md J-9). A stored one still renders, so it can be removed. */
                      (field !== 'project' && (remote || !RULE_REMOTE_FIELDS.has(field))),
                  ).map((field) => ({
                    label: t(FIELD_KEY[field]),
                    checked: condition.field === field,
                    /* ⚠️ Changing the field CLEARS the value — a uuid left behind after switching from
                 project to tag is a condition that matches nothing and looks filled in. */
                    onSelect: () => {
                      /* ⚠️ …and an operator the new field does not offer falls back to `eq`. */
                      const op = opsFor(field).includes(condition.op) ? condition.op : 'eq';
                      edit({
                        ...rule,
                        conditions: rule.conditions.map((one, at) =>
                          at === index
                            ? op === 'in'
                              ? { field, op, value: '', values: [] }
                              : { field, op, value: '' }
                            : one,
                        ),
                      });
                    },
                  }))}
                />
                <Picker
                  grow
                  width={128}
                  label={t('rule.op')}
                  value={t(OP_KEY[condition.op])}
                  disabled={!canManage}
                  items={opsFor(condition.field).map((op) => ({
                    label: t(OP_KEY[op]),
                    checked: condition.op === op,
                    onSelect: () => {
                      edit({
                        ...rule,
                        conditions: rule.conditions.map((one, at) =>
                          at === index ? withOp(one, op) : one,
                        ),
                      });
                    },
                  }))}
                />
                <div className="min-w-0 @max-[380px]:order-1 @max-[380px]:col-span-2">
                  {condition.op === 'in' ? (
                    <ValueList
                      condition={condition}
                      options={options}
                      disabled={!canManage}
                      onChange={(values) => {
                        setCondition(index, { value: '', values });
                      }}
                    />
                  ) : (
                    <Value
                      condition={condition}
                      options={options}
                      disabled={!canManage}
                      onChange={(value) => {
                        setCondition(index, { value });
                      }}
                    />
                  )}
                </div>
                {canManage ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground"
                    aria-label={t('rule.removeCondition')}
                    onClick={() => {
                      edit({
                        ...rule,
                        conditions: rule.conditions.filter((_, at) => at !== index),
                      });
                    }}
                  >
                    <Icon name="x" size={13} />
                  </Button>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        </div>

        {canManage ? (
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground -ml-2"
              onClick={() => {
                edit({
                  ...rule,
                  /* The mockup's own default first condition: a status somebody will change. */
                  conditions: [...rule.conditions, { field: 'tag', op: 'eq', value: '' }],
                });
              }}
            >
              <Icon name="plus" size={13} />
              {t('rule.addCondition')}
            </Button>
          </div>
        ) : null}

        {/* ⚠️ **The thrown-back tasks, as chips.** They are the only visible trace of a decision that
          otherwise looks like the rule quietly not matching — and without a way back, an accidental
          exclusion is permanent. */}
      </PanelField>

      {excluded.length === 0 ? null : (
        <PanelField label={t('rule.excludedLabel')}>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {excluded.map((task) => (
              <span
                key={task.id}
                className="bg-secondary text-secondary-foreground flex h-[22px] items-center gap-1 rounded-full pr-1 pl-2 text-[11px]"
              >
                {task.key}
                {canManage ? (
                  <button
                    type="button"
                    className="hover:text-destructive flex opacity-60 transition-colors"
                    aria-label={t('rule.restore', { key: task.key })}
                    onClick={() => {
                      onRestore(task.id);
                    }}
                  >
                    <Icon name="x" size={11} />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        </PanelField>
      )}
    </>
  );
}

/**
 * The rule as being edited, and `edit` — which changes it on screen at once and saves it once
 * `SAVE_AFTER_MS` has passed with no further change. A saved rule arriving from the server replaces
 * the draft only while nothing is waiting, so a reload landing mid-typing cannot undo the typing.
 */
function useDebouncedRule(
  saved: LineItemRule,
  save: (rule: LineItemRule) => void,
): { readonly rule: LineItemRule; readonly edit: (rule: LineItemRule) => void } {
  const [draft, setDraft] = useState(saved);
  const pending = useRef<LineItemRule | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    if (pending.current === null) setDraft(saved);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the CONTENT: the parent
       builds a fresh rule object every render. */
  }, [savedKey]);

  /* A draft still waiting when the editor closes is sent, not lost. */
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
      if (pending.current !== null) saveRef.current(pending.current);
    },
    [],
  );

  const edit = (next: LineItemRule): void => {
    setDraft(next);
    pending.current = next;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      const rule = pending.current;
      pending.current = null;
      if (rule !== null) saveRef.current(rule);
    }, SAVE_AFTER_MS);
  };

  return { rule: draft, edit };
}

function Picker({
  width,
  label,
  value,
  items,
  disabled,
  grow = false,
  search = false,
}: {
  readonly width: number;
  readonly label: string;
  readonly value: string;
  readonly items: MenuItem[];
  readonly disabled: boolean;
  readonly grow?: boolean;
  /** A filter above the items — a Jira site can have dozens of components. */
  readonly search?: boolean;
}) {
  const t = useT();
  if (disabled) {
    return (
      <span
        className="border-input text-foreground flex h-[30px] items-center rounded-md border px-2 text-[12px]"
        style={{ width: grow ? undefined : width }}
      >
        {value}
      </span>
    );
  }

  return (
    <DropdownMenu
      items={items}
      width={Math.max(200, width)}
      align="start"
      label={label}
      {...(search
        ? { searchPlaceholder: t('rule.searchValues'), emptyLabel: t('rule.noValues') }
        : {})}
      className={grow ? 'w-full min-w-0' : undefined}
    >
      <button
        type="button"
        className="border-input text-foreground hover:bg-accent/60 flex h-[30px] items-center gap-1.5 rounded-md border px-2 text-left text-[12px] shadow-xs transition-colors"
        style={{ width: grow ? '100%' : width }}
      >
        <span className="min-w-0 flex-1 truncate">{value}</span>
        <Icon name="chevron-down" size={12} className="text-muted-foreground opacity-60" />
      </button>
    </DropdownMenu>
  );
}

/**
 * ⚠️ **A picker for the four id-valued fields and a box for the three textual ones.** The mockup's
 * `hasOptions` / `isText` split, and its `valueOptions` vocabulary.
 */
function Value({
  condition,
  options,
  disabled,
  onChange,
}: {
  readonly condition: RuleCondition;
  readonly options: RuleOptions;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}) {
  const t = useT();
  const choices = optionsFor(condition.field, options);

  if (choices === null) {
    return (
      <TextInput
        value={condition.value}
        disabled={disabled}
        aria-label={t('rule.value')}
        placeholder={t(
          condition.field === 'key' ? 'rule.placeholder.key' : 'rule.placeholder.text',
        )}
        className="h-[30px] w-full min-w-0"
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    );
  }

  const current = choices.find((choice) => choice.id === condition.value);
  const remote = RULE_REMOTE_FIELDS.has(condition.field);
  return (
    <Picker
      grow
      width={140}
      label={t('rule.value')}
      /* ⚠️ A Jira value no synced issue carries any more is still what the rule holds — shown by
         its id rather than as an empty picker, which would claim the condition is unfinished. */
      value={
        current?.name ?? (remote && condition.value !== '' ? condition.value : t('rule.pickValue'))
      }
      disabled={disabled}
      /* 29 Oct 2026 · every value picker searches — a status list, a project's people — not only
         the Jira fields: a set of values is always search and dropdown. */
      search
      items={choices.map((choice) => ({
        label: choice.name,
        ...(choice.color === undefined ? {} : { dot: choice.color }),
        ...(choice.hint === undefined ? {} : { hint: choice.hint }),
        checked: condition.value === choice.id,
        onSelect: () => {
          onChange(choice.id);
        },
      }))}
    />
  );
}

/**
 * ⚠️ **An `in`: chips for what is chosen, and one control that adds.** A picker over the field's
 * choices where there are some, a text box that takes a value on Enter or comma where there are not
 * (`tag in [2026, uat]` is the legacy *labels IN* query, typed).
 */
function ValueList({
  condition,
  options,
  disabled,
  onChange,
}: {
  readonly condition: RuleCondition;
  readonly options: RuleOptions;
  readonly disabled: boolean;
  readonly onChange: (values: string[]) => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const chosen = condition.values ?? [];
  const choices = optionsFor(condition.field, options);
  const nameOf = (id: string): string => choices?.find((one) => one.id === id)?.name ?? id;

  const add = (raw: string): void => {
    const fresh = raw
      .split(',')
      .map((one) => one.trim())
      .filter((one) => one !== '' && !chosen.includes(one));
    if (fresh.length > 0) onChange([...chosen, ...fresh].slice(0, RULE_IN_MAX));
    setDraft('');
  };

  return (
    <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5">
      {chosen.map((id) => (
        <span
          key={id}
          className="bg-secondary text-secondary-foreground flex h-[22px] max-w-[220px] items-center gap-1 rounded-full pr-1 pl-2 text-[11px]"
        >
          <span className="truncate">{nameOf(id)}</span>
          {disabled ? null : (
            <button
              type="button"
              className="hover:text-destructive flex opacity-60 transition-colors"
              aria-label={t('rule.removeValue', { value: nameOf(id) })}
              onClick={() => {
                onChange(chosen.filter((one) => one !== id));
              }}
            >
              <Icon name="x" size={11} />
            </button>
          )}
        </span>
      ))}
      {disabled ? null : choices === null ? (
        <TextInput
          value={draft}
          aria-label={t('rule.value')}
          placeholder={t('rule.placeholder.list')}
          className="h-[30px] min-w-[120px] flex-1"
          onChange={(event) => {
            const next = event.target.value;
            if (next.includes(',')) add(next);
            else setDraft(next);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add(draft);
            }
          }}
          onBlur={() => {
            if (draft.trim() !== '') add(draft);
          }}
        />
      ) : (
        <Picker
          width={140}
          label={t('rule.addValue')}
          value={chosen.length === 0 ? t('rule.pickValues') : t('rule.addValue')}
          disabled={false}
          search
          items={choices
            .filter((choice) => !chosen.includes(choice.id))
            .map((choice) => ({
              label: choice.name,
              ...(choice.color === undefined ? {} : { dot: choice.color }),
              ...(choice.hint === undefined ? {} : { hint: choice.hint }),
              onSelect: () => {
                add(choice.id);
              },
            }))}
        />
      )}
    </div>
  );
}

/**
 * 29 Oct 2026 · one condition in a few words — *Státusz = Backlog*, *Címke: uat, release* — for the
 * summary pill above a line item's tasks. Ids read as their names; an unfinished condition says so.
 */
export function conditionSummary(
  t: Translate,
  condition: RuleCondition,
  options: RuleOptions,
): string {
  const field = t(FIELD_KEY[condition.field]);
  const choices = optionsFor(condition.field, options);
  const nameOf = (value: string): string => choices?.find((one) => one.id === value)?.name ?? value;
  if (condition.op === 'in') {
    const values = (condition.values ?? []).map(nameOf);
    return values.length === 0 ? `${field}: …` : `${field}: ${values.join(', ')}`;
  }
  const value = condition.value === '' ? '…' : nameOf(condition.value);
  if (condition.op === 'eq') return `${field} = ${value}`;
  if (condition.op === 'ne') return `${field} ≠ ${value}`;
  return `${field} ${t(OP_KEY[condition.op])} ${value}`;
}

function optionsFor(
  field: RuleField,
  options: RuleOptions,
): { id: string; name: string; color?: string; hint?: string }[] | null {
  if (field === 'project') return [...options.projects];
  if (field === 'status') return [...options.statuses];
  if (field === 'priority') return [...options.priorities];
  if (field === 'assignee') return [...options.members];
  /* ⚠️ The count is how many of the project's tasks carry it — what choosing it would catch. */
  const remote = (entries: readonly RuleVocabularyEntry[]) =>
    entries.map((entry) => ({ id: entry.id, name: entry.name, hint: String(entry.taskCount) }));
  if (field === 'component') return remote(options.components);
  if (field === 'fix_version') return remote(options.fixVersions);
  if (field === 'epic') return remote(options.epics);
  return null;
}
